import { useRef } from 'react';
import { useAtomValue } from 'jotai';
import { cn } from '@rocco/utils/cn';
import type { TileViewModel } from '@rocco/ui/tile-shared/view-models/tile';
import type { ProductTileViewModel } from '@rocco/ui/tile-product/view-models/product';
import { TileMedia } from '@rocco/ui/tile-shared/components/TileMedia';
import { TileBadge } from '@rocco/ui/tile-shared/components/TileBadge';
import { ProductTileAddToCartButton } from '@rocco/ui/tile-product/components/ProductTileAddToCartButton';
import { ProductTileVariants } from './ProductTileVariants';
import { ProductTilePrice } from './ProductTilePrice';
import { TileFollowButton } from '@rocco/ui/tile-shared/components/TileFollowButton';
import { uiContextAtomTileActions } from '@rocco/ui/tile-shared/hooks/use-shared-tiles';
import { uiContextAtomDisplayMode } from '@rocco/states/uiContextAtomDisplayMode';
import { isSalesModeAtom } from '@rocco/states/apAdminUiContext';
import { useTracker } from '../../../../../tracker';
import { trackTileMediaDetailClick } from '../../../../../web/app/modules/tracking/util/trackTileMedia';
import { OptionalLink } from '@rocco/components/link/OptionalLink';
import { TILE_COMPONENTS_CLASS_NAMES } from '@rocco/ui/tile-shared/utils/contstants';

export const ProductTile = (props: TileViewModel<ProductTileViewModel>) => {
    const {
        aspectRatio,
        model: product,
        section,
        disableDesignBoard,
        enableTileSlider,
        disableTileSliderABTest,
        isFollowed,
        onFollow,
        hideFollowButton = false,
        inlineActionButtons,
        disableLink = false,
        onClick,
        className,
    } = props;
    const { isMobile } = useAtomValue(uiContextAtomDisplayMode);
    const tileActions = useAtomValue(uiContextAtomTileActions);
    const tileRef = useRef<HTMLAnchorElement>(null);
    const tracker = useTracker();
    const isSalesMode = useAtomValue(isSalesModeAtom);

    const draftOrPending = product.draft || product.pendingApproval;
    const statusLabel = draftOrPending
        ? product.draft
            ? 'Draft'
            : 'Pending Approval'
        : product.badge;

    const handleTileClick = () => {
        onClick?.();
        if (disableLink) {
            return;
        }

        tileActions?.trackingConfig?.onClick?.(
            tileRef,
            product.id,
            product.professional.id,
            section
        );

        trackTileMediaDetailClick(tracker, {
            itemID: product.id,
            itemType: 'ProductTile',
            itemName: product.title,
        });
    };

    return (
        <OptionalLink
            ref={tileRef}
            mode={disableLink ? 'override' : 'normal'}
            to={product.link}
            prefetch="intent"
            onClick={handleTileClick}
        >
            <div className={cn('flex h-full flex-col gap-4', className)}>
                <div className="group relative h-auto">
                    <TileMedia
                        aspectRatio={aspectRatio}
                        images={product.images}
                        video={product.video}
                        enableTileSlider={enableTileSlider}
                        disableTileSliderABTest={disableTileSliderABTest}
                        onSlideChange={(direction) => {
                            tileActions?.trackingConfig?.onTileMediaSliderSlideChange?.(
                                direction,
                                product.id
                            );
                        }}
                    />

                    <div className="absolute inset-0 hidden rounded-tile bg-gray-900/10 opacity-0 transition-opacity duration-300 md:block md:group-hover:opacity-100" />

                    {statusLabel && (
                        <div className="absolute left-4 top-4">
                            <TileBadge title={statusLabel} />
                        </div>
                    )}

                    <div className="absolute right-4 top-3.5 flex gap-1 transition-opacity duration-300 md:invisible md:opacity-0 md:group-hover:visible md:group-hover:opacity-100">
                        {!hideFollowButton && (
                            <TileFollowButton
                                pinId={product.id}
                                pinType="ProductPin"
                                disabled={disableDesignBoard}
                                isFollowed={isFollowed}
                                onFollow={onFollow}
                            />
                        )}
                        {product.canPurchase && (
                            <ProductTileAddToCartButton to={product.link} />
                        )}
                    </div>
                </div>

                <div className="flex flex-col gap-4">
                    {inlineActionButtons && (
                        <div className="flex flex-col">
                            {inlineActionButtons}
                        </div>
                    )}
                    <div
                        className={cn(
                            TILE_COMPONENTS_CLASS_NAMES.TILE_DESCRIPTION_BLOCK,
                            'flex flex-col'
                        )}
                    >
                        <span className="line-clamp-1 overflow-hidden text-ellipsis text-sm font-medium tracking-[0.03rem] md:text-base md:tracking-[0.02rem]">
                            {product.title}
                        </span>
                        <span className="line-clamp-1 overflow-hidden text-ellipsis text-sm font-medium tracking-[0.03rem] text-gray-900/40 md:text-base md:tracking-[0.02rem]">
                            {product.professional.name}
                        </span>
                    </div>

                    {product.price && !isSalesMode ? (
                        <div className="flex items-center justify-between gap-2 text-sm text-gray-900/40 md:text-base">
                            <ProductTilePrice price={product.price} />
                            {product.variants && (
                                <ProductTileVariants
                                    variants={product.variants}
                                    limit={isMobile ? 3 : 5}
                                />
                            )}
                        </div>
                    ) : null}
                </div>
            </div>
        </OptionalLink>
    );
};
